import { Cpu } from 'lucide-react';
import { useMemo, type FC } from 'react';

interface ModelEntry {
  model: string;
  tokens: number;
  apiCalls: number;
  cost: number;
}

interface Props {
  byModel: ModelEntry[] | undefined;
}

const MODEL_COLORS: Record<string, string> = {
  'claude-sonnet': 'bg-blue-500 dark:bg-blue-400',
  'claude-haiku': 'bg-violet-500 dark:bg-violet-400',
  whisper: 'bg-amber-500 dark:bg-amber-400',
};

const MODEL_TEXT: Record<string, string> = {
  'claude-sonnet': 'text-blue-700 dark:text-blue-300',
  'claude-haiku': 'text-violet-700 dark:text-violet-300',
  whisper: 'text-amber-700 dark:text-amber-300',
};

const ModelBreakdownChart: FC<Props> = ({ byModel }) => {
  const { models, total } = useMemo(() => {
    const items = byModel ?? [];
    const sum = items.reduce((s, m) => s + m.tokens, 0);
    const sorted = [...items].sort((a, b) => b.tokens - a.tokens);
    return { models: sorted, total: sum || 1 };
  }, [byModel]);

  return (
    <div className="fc-card p-5">
      <div className="flex items-center gap-2">
        <Cpu className="h-4 w-4 text-slate-500 dark:text-slate-400" />
        <h3 className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Ventilation par Modèle
        </h3>
      </div>

      <div className="mt-4 space-y-3">
        {models.map((m) => {
          const pct = Math.round((m.tokens / total) * 100);
          const key = m.model.toLowerCase();
          const barColor = MODEL_COLORS[key] ?? 'bg-slate-400 dark:bg-slate-500';
          const textColor = MODEL_TEXT[key] ?? 'text-slate-700 dark:text-slate-300';

          return (
            <div key={m.model}>
              <div className="flex items-center justify-between text-sm">
                <span className={`font-medium ${textColor}`}>{m.model}</span>
                <span className="text-slate-500 dark:text-slate-400">
                  {pct}% — {m.tokens.toLocaleString('fr-FR')} tokens
                </span>
              </div>
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                <div
                  className={`h-full rounded-full ${barColor} transition-all duration-500`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ModelBreakdownChart;
